import { c as _c } from "react/compiler-runtime";
import { Button, Divider, IconButton, Stack, Typography } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useCallback, useState } from 'react';
import { CloudProviders } from '@/assets/logos/cloudProviders';
import { AccountIndicator } from '@/components/accounts/accountIndicator';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { CopyToClipboardWrapper } from '@/components/buttons/copyToClipboardButton';
import { LabelsEditor } from '@/components/labels/labelsEditor';
import { Icon } from '@/components/shared/icon';
import { TagsElasticContainer } from '@/components/tags/tagsElasticContainer';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import type { BackupVault } from '@/data/vaults/backupVault';
import { VaultColorSelectorWithButtons } from './createBackupVaultPanel';
import { GcpKmsCopyablePolicy } from './gcpKmsCopyablePolicy';
import { KmsSuggestedPolicyContent } from './kmsSuggestedPolicy';
import { useBackupVaultColorUpdate } from './useBackupVaultColorUpdate';
import { VaultColoredDot } from './vaultColoredDot';
import { RenameEntityName } from '../wizard/renameEntityName';

// A vault carries a key only when the customer supplied one; without it the vault is encrypted with
// Eon's own key. Only the vault's own provider block can hold one, so any of them answers for it.
const hasCustomerEncryptionKey = (vault: BackupVault) => !!(vault.vaultAttributes.aws?.encryptionKey || vault.vaultAttributes.azure?.encryptionKey || vault.vaultAttributes.gcp?.encryptionKey);
interface UseVaultDetailsDialogOptions {
  editable: boolean;
  onSaveSuccess?: () => void;
}
export function useVaultDetailsDialog(t0) {
  const $ = _c(5);
  const isLoading = t0 === undefined ? false : t0;
  const {
    sharedDialog
  } = useWorkspace();
  let t1;
  if ($[0] !== isLoading || $[1] !== sharedDialog) {
    t1 = (vault, options) => {
      const openKmsSuggestedPolicy = () => {
        sharedDialog.open("vault-details-kms-policy", <KmsSuggestedPolicyContent kmsKey={vault.vaultAttributes.aws?.encryptionKey} cloudProvider={vault.vaultAttributes.cloudProvider} />, {
          isLoading,
          header: {
            title: "Get suggested policy",
            subtitle: "To restore from your self-managed vault, add this policy to the KMS key in your AWS account. Without it, restoring can fail."
          }
        });
      };
      const openGcpKmsSuggestedPolicy = () => {
        sharedDialog.open("vault-details-gcp-kms-policy", <GcpKmsSuggestedPolicyContent backupVault={vault} />, {
          isLoading,
          header: {
            title: "Get suggested permissions",
            subtitle: "Run these commands to grant the required service accounts access to your Cloud KMS key. Without these permissions, vault operations may fail."
          }
        });
      };
      sharedDialog.open("vault-details", <VaultDetailsContent vault={vault} vaultAccountId={vault.vaultAccountId} editable={options.editable} onSaveSuccess={options.onSaveSuccess ?? _temp} onOpenKmsSuggestedPolicy={openKmsSuggestedPolicy} onOpenGcpKmsSuggestedPolicy={openGcpKmsSuggestedPolicy} />, {
        isLoading,
        header: {
          title: "Vault Details",
          subtitle: "View the vault's configuration. Only the name, color and labels can be edited."
        }
      });
    };
    $[0] = isLoading;
    $[1] = sharedDialog;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const open = t1;
  let t2;
  if ($[3] !== open) {
    t2 = {
      open
    };
    $[3] = open;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function _temp() {}
const VaultDetailsContent = ({
  vault: initialVault,
  vaultAccountId,
  editable,
  onSaveSuccess,
  onOpenKmsSuggestedPolicy,
  onOpenGcpKmsSuggestedPolicy
}: {
  vault: BackupVault;
  vaultAccountId?: string;
  editable: boolean;
  onSaveSuccess: () => void;
  onOpenKmsSuggestedPolicy: () => void;
  onOpenGcpKmsSuggestedPolicy: () => void;
}) => {
  const [vault, setVault] = useState(initialVault);
  const [isEditingColor, setIsEditingColor] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedLabels, setEditedLabels] = useState<string[]>();
  const {
    isAuthorized
  } = useRoles();
  const isEditable = editable && isAuthorized('update:vaults');
  const dal = useDAL();
  const {
    updateVaultColor
  } = useBackupVaultColorUpdate();
  const {
    EnableCspAssistedImmutability = false,
    PolicyAndVaultMetadataLabels = false
  } = useFeatureFlags();
  const vaultAccounts = dal.cloudAccounts.vault.useList();
  const vaultAccount = vaultAccounts.data?.accounts.find(account => account.id === vaultAccountId);
  const accountLabel = vaultAccount?.cloudProvider === Provider.Azure ? 'Subscription' : vaultAccount?.cloudProvider === Provider.Gcp ? 'Project' : 'Account';
  return <Stack divider={<Divider sx={{
    borderStyle: 'dashed'
  }} />} sx={{
    marginBottom: '16px',
    gap: '16px'
  }}>
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          Name
        </Typography>
        {isEditable ? <RenameEntityName name={vault.name} onSubmit={name => {
        const updatedVault = {
          ...vault,
          name
        };
        void dal.vaults.update(updatedVault).then(() => {
          setVault(updatedVault);
          setIsEditingName(false);
          onSaveSuccess();
        });
      }} bold={false} editMode={isEditingName} setEditMode={setIsEditingName} /> : <Typography>{vault.name}</Typography>}
      </RowWrapper>
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          ID
        </Typography>
        <Stack direction='row' sx={{
        gap: '16px',
        alignItems: 'center'
      }}>
          <CopyToClipboardWrapper textToCopy={vault.id}>
            <Typography>{vault.id}</Typography>
          </CopyToClipboardWrapper>
        </Stack>
      </RowWrapper>
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          Color
        </Typography>
        {isEditingColor && isEditable ? <VaultColorSelectorWithButtons initialData={vault} saveData={vault_0 => {
        void updateVaultColor(vault_0).then(() => {
          setVault(vault_0);
          setIsEditingColor(false);
          onSaveSuccess();
        });
      }} onCancel={() => setIsEditingColor(false)} disabled={false} /> : <Stack direction='row' sx={{
        alignItems: 'center',
        gap: '12px'
      }}>
            <VaultColoredDot color={vault.backgroundColor} />
            {isEditable && <IconButton onClick={() => setIsEditingColor(true)} data-testid='rename-entity-name-edit-btn'>
                <i className={'material-symbols-stylus-outline w-[20px] h-[20px] text-(--mui-palette-text-primary)'} />
              </IconButton>}
          </Stack>}
      </RowWrapper>
      {PolicyAndVaultMetadataLabels &&
    // The labels grow down the row - a wrapped set of chips, and the editor with its controls
    // below - so the key sits at the top rather than in the middle of all that.
    <RowWrapper alignItems='flex-start'>
          <Typography sx={{
        width: '150px'
      }}>
            Labels
          </Typography>
          {editedLabels ? <Stack sx={{
        flexGrow: 1,
        padding: '24px',
        borderRadius: '16px',
        border: '1px solid var(--mui-palette-divider)'
      }}>
              <LabelsEditor labels={editedLabels} onChange={setEditedLabels} addOnEmpty />
              {/* Separates the labels being edited from the controls that commit them, in the same
                  dashed rule the dialog uses between its rows. */}
              <Divider sx={{
          borderStyle: 'dashed',
          marginTop: '24px'
        }} />
              <Stack direction='row' sx={{
          gap: '8px',
          paddingTop: '16px',
          justifyContent: 'flex-end'
        }}>
                <Button variant='text' onClick={() => setEditedLabels(undefined)}>
                  Cancel
                </Button>
                <Button variant='contained' onClick={() => {
            const updatedVault_0 = {
              ...vault,
              labels: editedLabels
            };
            void dal.vaults.update(updatedVault_0).then(() => {
              setVault(updatedVault_0);
              setEditedLabels(undefined);
              onSaveSuccess();
            });
          }}>
                  Save
                </Button>
              </Stack>
            </Stack> : <Stack direction='row' sx={{
        alignItems: 'center',
        gap: '8px'
      }}>
              {vault.labels?.length ? <TagsElasticContainer tags={vault.labels} expandable /> : <Typography>--</Typography>}
              {isEditable && <IconButton onClick={() => {
          setEditedLabels([...(vault.labels ?? [])]);
        }} data-testid='vault-labels-edit-btn'>
                  <Icon iconClass='material-symbols-stylus-outline' color='var(--mui-palette-text-primary)' />
                </IconButton>}
            </Stack>}
        </RowWrapper>}
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          Cloud
        </Typography>
        <Typography>
          {CloudProviders[vault.vaultAttributes.cloudProvider].displayName}
        </Typography>
      </RowWrapper>
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          Region
        </Typography>
        <Typography>{vault.region}</Typography>
      </RowWrapper>
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          Type
        </Typography>
        <Typography>
          {vaultAccount && !vaultAccount.isCustomerOwned ? 'Eon-managed' : 'Self-managed'}
        </Typography>
      </RowWrapper>
      {EnableCspAssistedImmutability && vault?.enableCspAssistedImmutability && <RowWrapper>
            <Typography sx={{
        width: '150px'
      }}>Immutability</Typography>
            <Typography>CSP assisted immutability</Typography>
          </RowWrapper>}
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          {accountLabel}
        </Typography>
        {vaultAccount ? <AccountIndicator account={vaultAccount} /> : '--'}
      </RowWrapper>
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          Encryption type
        </Typography>
        <Typography>
          {hasCustomerEncryptionKey(vault) ? 'Self-managed' : 'Eon-managed'}
        </Typography>
      </RowWrapper>
      {vaultAccount?.isCustomerOwned && vault.vaultAttributes.aws?.bucketName && <RowWrapper>
            <Typography sx={{
        width: '150px',
        flexShrink: 0
      }}>
              Bucket
            </Typography>
            <Stack direction='row' sx={{
        flex: 1,
        minWidth: '0px',
        alignItems: 'center',
        gap: '16px'
      }}>
              <Stack sx={{
          flex: 1,
          minWidth: 0
        }}>
                <Stack sx={{
            width: '170px',
            maxWidth: '100%'
          }}>
                  <CopyToClipboardWrapper size='medium' textToCopy={vault.vaultAttributes.aws.bucketName}>
                    <OverflowTooltip text={vault.vaultAttributes.aws.bucketName} />
                  </CopyToClipboardWrapper>
                </Stack>
              </Stack>
            </Stack>
          </RowWrapper>}
      {vaultAccount?.isCustomerOwned && vault.vaultAttributes.gcp?.bucketName && <RowWrapper>
            <Stack direction='row' sx={{
        gap: '16px'
      }}>
              <Typography sx={{
          width: '150px'
        }}>
                Bucket
              </Typography>
              <Typography>{vault.vaultAttributes.gcp?.bucketName}</Typography>
            </Stack>
          </RowWrapper>}
      {vaultAccount?.isCustomerOwned && vault.vaultAttributes.azure?.storageAccount && <RowWrapper>
            <Stack direction='row' sx={{
        gap: '16px'
      }}>
              <Typography sx={{
          width: '150px'
        }}>
                Storage Account
              </Typography>
              <Typography>
                {vault.vaultAttributes.azure?.storageAccount}
              </Typography>
            </Stack>
          </RowWrapper>}
      {vault.vaultAttributes.aws?.encryptionKey && <RowWrapper justifyContent='space-between'>
          <Stack direction='row' sx={{
        gap: '16px'
      }}>
            <Typography sx={{
          width: '150px'
        }}>
              KMS Key
            </Typography>
            <Stack sx={{
          width: '170px'
        }}>
              <CopyToClipboardWrapper size='medium' textToCopy={vault.vaultAttributes.aws?.encryptionKey}>
                <OverflowTooltip text={vault.vaultAttributes.aws?.encryptionKey} />
              </CopyToClipboardWrapper>
            </Stack>
          </Stack>
          <Stack sx={{
        width: '180px',
        flexShrink: 0,
        alignItems: 'flex-start'
      }}>
            <ButtonWithIcon variant='text' text='Get Suggested Policy' icon='material-symbols-data-object' onClick={onOpenKmsSuggestedPolicy} />
          </Stack>
        </RowWrapper>}
      {vault.vaultAttributes.gcp?.encryptionKey && <RowWrapper justifyContent='space-between'>
          <Stack direction='row' sx={{
        gap: '16px'
      }}>
            <Typography sx={{
          width: '150px'
        }}>
              Cloud KMS Key
            </Typography>
            <Stack sx={{
          width: '170px'
        }}>
              <OverflowTooltip text={vault.vaultAttributes.gcp?.encryptionKey} />
            </Stack>
          </Stack>
          <ButtonWithIcon variant='text' text='Get Suggested Permissions' icon='material-symbols-data-object' onClick={onOpenGcpKmsSuggestedPolicy} />
        </RowWrapper>}
      {vault.vaultAttributes.azure?.encryptionKey && <RowWrapper>
          <Stack direction='row' sx={{
        gap: '16px'
      }}>
            <Typography sx={{
          width: '150px'
        }}>
              Key Vault key
            </Typography>
            <Stack sx={{
          width: '170px'
        }}>
              <OverflowTooltip text={vault.vaultAttributes.azure?.encryptionKey} />
            </Stack>
          </Stack>
        </RowWrapper>}
    </Stack>;
};
const GcpKmsSuggestedPolicyContent = ({
  backupVault
}: {
  backupVault: BackupVault;
}) => {
  const dal = useDAL();
  const {
    data: scanningAccounts
  } = dal.cloudAccounts.scanning.useList();
  const gcpScanningAccount = scanningAccounts?.accounts.find(a => a.cloudProvider === Provider.Gcp);
  if (!gcpScanningAccount?.id) {
    return null;
  }
  return <Stack sx={{
    paddingX: '40px',
    gap: '24px',
    alignItems: 'flex-start'
  }}>
      <GcpKmsCopyablePolicy backupVault={backupVault} scanningAccountId={gcpScanningAccount.id} />
    </Stack>;
};
const RowWrapper = t0 => {
  const $ = _c(6);
  const {
    children,
    justifyContent,
    alignItems: t1
  } = t0;
  const alignItems = t1 === undefined ? "center" : t1;
  let t2;
  if ($[0] !== alignItems || $[1] !== justifyContent) {
    t2 = {
      minHeight: "32px",
      flexGrow: 1,
      paddingRight: "24px",
      paddingLeft: "40px",
      gap: "16px",
      alignItems,
      justifyContent
    };
    $[0] = alignItems;
    $[1] = justifyContent;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== t2) {
    t3 = <Stack direction="row" sx={t2}>{children}</Stack>;
    $[3] = children;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};